import { KeyboardEvent, ReactNode } from 'react';
import { EmptyState as PxEmptyState } from '@roi-pros/px-react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
}

/**
 * The admin console's table: a sticky header, comfortable rows with a hover
 * tint, whole-row click when `onRowClick` is given (and Enter on a focused
 * row), and a row action column (`key: 'actions'`) that appears on hover or
 * focus on desktop. Below `md` the same rows render as stacked cards, each
 * cell labelled by its column header — one DOM, no duplicated content.
 */
export function Table<T extends Record<string, unknown>>({
  columns,
  data,
  keyField,
  onRowClick,
  emptyMessage = 'No data available',
}: TableProps<T>) {
  // B5 Platform adoption: the empty state renders via the Platform Experience
  // canonical empty state (@roi-pros/px-react). The application decides WHEN
  // the table is empty and WHAT it says; the Platform owns HOW it renders.
  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8">
        <PxEmptyState model={{ kind: 'empty', title: emptyMessage }} />
      </div>
    );
  }

  const onKey = (row: T) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (onRowClick && e.key === 'Enter' && e.target === e.currentTarget) onRowClick(row);
  };

  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 max-md:overflow-visible max-md:ring-0">
      <table className="min-w-full border-separate border-spacing-0 max-md:block">
        <thead className="max-md:hidden">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={`sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 backdrop-blur whitespace-nowrap first:rounded-tl-xl last:rounded-tr-xl ${col.className || ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white max-md:block max-md:space-y-3 max-md:bg-transparent">
          {data.map((row) => (
            <tr
              key={String(row[keyField])}
              onClick={() => onRowClick?.(row)}
              onKeyDown={onKey(row)}
              tabIndex={onRowClick ? 0 : undefined}
              className={`group transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40 focus-visible:outline-none focus-visible:bg-emerald-50/60 max-md:block max-md:rounded-xl max-md:bg-white max-md:p-3 max-md:shadow-sm max-md:ring-1 max-md:ring-slate-200 ${
                onRowClick ? 'cursor-pointer' : ''
              }`}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  data-label={col.header || undefined}
                  className={`border-b border-slate-100 px-4 py-3.5 align-middle text-sm text-slate-700 group-last:border-b-0 max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:border-0 max-md:px-1 max-md:py-1.5 max-md:before:shrink-0 max-md:before:text-xs max-md:before:font-medium max-md:before:text-slate-500 max-md:before:content-[attr(data-label)] ${
                    col.key === 'actions'
                      ? 'w-12 text-right md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100 md:group-focus-visible:opacity-100 max-md:justify-end'
                      : ''
                  } ${col.className || ''}`}
                >
                  {col.render ? col.render(row) : String(row[col.key as keyof T] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Shared classes for the hand-built tables, so every table in the console matches. */
export const tableClasses = {
  wrap: 'overflow-x-auto rounded-xl ring-1 ring-slate-200',
  table: 'min-w-full border-separate border-spacing-0 text-sm',
  th: 'sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap',
  tr: 'group transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40',
  td: 'border-b border-slate-100 px-4 py-3.5 align-middle text-slate-700',
} as const;
